import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Flip } from "gsap/Flip";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  Button,
  Input,
  TextField,
} from "react-aria-components";

import backIcon from "@/assets/figma/my-files/core/back.svg";
import capacityIcon from "@/assets/figma/my-files/core/capacity.svg";
import closeIcon from "@/assets/figma/my-files/core/close.svg";
import conversationIcon from "@/assets/figma/my-files/core/conversation.svg";
import deleteIcon from "@/assets/figma/my-files/core/delete.svg";
import dimensionsIcon from "@/assets/figma/my-files/core/dimensions.svg";
import downloadIcon from "@/assets/figma/my-files/core/download.svg";
import externalLinkIcon from "@/assets/figma/my-files/core/external-link.svg";
import fileDocIcon from "@/assets/figma/my-files/core/file-doc.svg";
import fileDocLightIcon from "@/assets/figma/my-files/core/file-doc-light.svg";
import filePdfIcon from "@/assets/figma/my-files/core/file-pdf.svg";
import filePdfLightIcon from "@/assets/figma/my-files/core/file-pdf-light.svg";
import filePptIcon from "@/assets/figma/my-files/core/file-ppt.svg";
import filePptLightIcon from "@/assets/figma/my-files/core/file-ppt-light.svg";
import filePreviewDocIcon from "@/assets/figma/my-files/core/file-preview-doc.svg";
import filePreviewPdfIcon from "@/assets/figma/my-files/core/file-preview-pdf.svg";
import filePreviewPptIcon from "@/assets/figma/my-files/core/file-preview-ppt.svg";
import filePreviewXlsxIcon from "@/assets/figma/my-files/core/file-preview-xlsx.svg";
import fileXlsxIcon from "@/assets/figma/my-files/core/file-xlsx.svg";
import fileXlsxLightIcon from "@/assets/figma/my-files/core/file-xlsx-light.svg";
import gridIcon from "@/assets/figma/my-files/core/grid.svg";
import imageIcon from "@/assets/figma/my-files/core/image.svg";
import imageLightIcon from "@/assets/figma/my-files/core/image-light.svg";
import listIcon from "@/assets/figma/my-files/core/list.svg";
import loadArrowIcon from "@/assets/figma/my-files/core/load-arrow.svg";
import loadDividerLeft from "@/assets/figma/my-files/core/load-divider-left.svg";
import loadDividerRight from "@/assets/figma/my-files/core/load-divider-right.svg";
import manageIcon from "@/assets/figma/my-files/core/manage.svg";
import moreIcon from "@/assets/figma/my-files/core/more.svg";
import newChatIcon from "@/assets/figma/my-files/core/new-chat.svg";
import newChatLightIcon from "@/assets/figma/my-files/core/new-chat-light.svg";
import searchIcon from "@/assets/figma/my-files/core/search.svg";
import sourceUnderline from "@/assets/figma/my-files/core/source-underline.svg";
import sourceUnderlineLight from "@/assets/figma/my-files/core/source-underline-light.svg";
import timeIcon from "@/assets/figma/my-files/core/time.svg";
import unsupportedFileIcon from "@/assets/figma/my-files/core/unsupported-file.svg";
import uploadIcon from "@/assets/figma/my-files/core/upload.svg";
import videoIcon from "@/assets/figma/my-files/core/video.svg";
import editIcon from "@/assets/figma/settings/edit.svg";
import { cn } from "@/shared/lib/cn";
import { MerryCheckbox } from "@/shared/ui/MerryCheckbox";
import { MerryButton } from "@/shared/ui/MerryButton";
import { MerrySearchField } from "@/shared/ui/MerrySearchField";
import { MerryDropdownMenu } from "@/shared/ui/components/MerryDropdownMenu";
import { MerryLabelTag } from "@/shared/ui/MerryLabelTag";
import {
  getMerryMyFilesBrowserVisualState,
  getMerryMyFilesDetailVisualState,
  getMerryMyFilesVariant,
  type MerryMyFilesLayout,
  type MerryMyFilesPreview,
  type MerryMyFilesVisualState,
} from "@/shared/ui/merryMyFilesVariants";

gsap.registerPlugin(Flip, useGSAP);

export type MerryMyFilesType =
  | "image"
  | "video"
  | "file"
  | "pdf"
  | "doc"
  | "ppt"
  | "xlsx";

export type MerryMyFilesAction =
  | "preview"
  | "download"
  | "chat"
  | "rename"
  | "delete";

export interface MerryMyFilesItem {
  availableActions?: ReadonlyArray<MerryMyFilesAction>;
  capacityLabel: string;
  createdAtLabel: string;
  description: string;
  dimensionsLabel: string;
  extension?: string;
  id: string;
  name: string;
  preview: MerryMyFilesPreview;
  previewSource?: string;
  previewText?: ReadonlyArray<string>;
  referenceSources: ReadonlyArray<string>;
  revision?: number;
  sourceConversationId?: string;
  sourceLabel: string;
  timeLabel: string;
  type: MerryMyFilesType;
}

export interface MerryMyFilesSection {
  id: string;
  items: ReadonlyArray<MerryMyFilesItem>;
  label: string;
}

export interface MerryMyFilesCatalog {
  loadMoreLabel: string;
  sections: ReadonlyArray<MerryMyFilesSection>;
}

export interface MerryMyFilesBrowserState {
  isManaging: boolean;
  layout: MerryMyFilesLayout;
  page: "browser";
}

export interface MerryMyFilesDetailState {
  fileId: string;
  page: "detail";
}

export type MerryMyFilesPanelState =
  | MerryMyFilesBrowserState
  | MerryMyFilesDetailState;

export interface MerryMyFilesPanelProps {
  "aria-label"?: string;
  "data-testid"?: string;
  catalog: MerryMyFilesCatalog;
  className?: string;
  defaultSelectedIds?: ReadonlyArray<string>;
  defaultState?: MerryMyFilesPanelState;
  errorMessage?: string;
  filter?: MerryMyFilesFilter;
  isLoading?: boolean;
  isUploading?: boolean;
  noticeMessage?: string;
  onChat?: (item: MerryMyFilesItem) => void;
  onClose?: () => void;
  onDelete?: (items: ReadonlyArray<MerryMyFilesItem>) => void;
  onDownload?: (items: ReadonlyArray<MerryMyFilesItem>) => void;
  onLoadMore?: () => void;
  onOpenConversation?: (item: MerryMyFilesItem) => void;
  onRename?: (
    item: MerryMyFilesItem,
    displayNameBase: string,
  ) => Promise<void> | void;
  onRetry?: () => void;
  onSearch?: () => void;
  onSearchQueryChange?: (query: string) => void;
  onFilterChange?: (filter: MerryMyFilesFilter) => void;
  onSelectedIdsChange?: (ids: ReadonlyArray<string>) => void;
  onStateChange?: (state: MerryMyFilesPanelState) => void;
  onUpload?: () => void;
  selectedIds?: ReadonlyArray<string>;
  searchQuery?: string;
  state?: MerryMyFilesPanelState;
}

export type MerryMyFilesFilter = "all" | "image" | "video" | "file";

type MerryMyFilesStyle = CSSProperties & {
  "--merry-my-files-height": string;
  "--merry-my-files-width": string;
};

const typeIcons: Record<MerryMyFilesType, string> = {
  doc: fileDocIcon,
  file: fileDocIcon,
  image: imageIcon,
  pdf: filePdfIcon,
  ppt: filePptIcon,
  video: videoIcon,
  xlsx: fileXlsxIcon,
};

const previewTypeIcons: Record<MerryMyFilesType, string> = {
  doc: filePreviewDocIcon,
  file: filePreviewDocIcon,
  image: imageIcon,
  pdf: filePreviewPdfIcon,
  ppt: filePreviewPptIcon,
  video: videoIcon,
  xlsx: filePreviewXlsxIcon,
};

const lightTypeIcons: Record<MerryMyFilesType, string> = {
  doc: fileDocLightIcon,
  file: fileDocLightIcon,
  image: imageLightIcon,
  pdf: filePdfLightIcon,
  ppt: filePptLightIcon,
  video: imageLightIcon,
  xlsx: fileXlsxLightIcon,
};

const filterLabels: Record<MerryMyFilesFilter, string> = {
  all: "全部",
  file: "文件",
  image: "图片",
  video: "视频",
};

const filterValues = ["all", "image", "video", "file"] as const;

const defaultBrowserState: MerryMyFilesBrowserState = {
  isManaging: false,
  layout: "list",
  page: "browser",
};

const motion = {
  contentEnter: 0.18,
  contentExit: 0.14,
  layout: 0.42,
  resize: 0.32,
} as const;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isFilter(value: string): value is MerryMyFilesFilter {
  return filterValues.some((filter) => filter === value);
}

function isMediaType(type: MerryMyFilesType) {
  return type === "image" || type === "video";
}

function matchesFilter(item: MerryMyFilesItem, filter: MerryMyFilesFilter) {
  if (filter === "all") return true;
  if (filter === "image") return item.type === "image";
  if (filter === "video") return item.type === "video";
  return !isMediaType(item.type);
}

function getDisplayNameBase(item: MerryMyFilesItem) {
  const extension = item.extension ?? "";
  if (
    extension &&
    item.name.toLocaleLowerCase().endsWith(extension.toLocaleLowerCase())
  ) {
    return item.name.slice(0, -extension.length);
  }
  return item.name;
}

function hasAction(item: MerryMyFilesItem, action: MerryMyFilesAction) {
  return item.availableActions?.includes(action) ?? true;
}

function findItem(
  catalog: MerryMyFilesCatalog,
  fileId: string,
): MerryMyFilesItem | undefined {
  return catalog.sections
    .flatMap((section) => section.items)
    .find((item) => item.id === fileId);
}

function getVisualState(
  state: MerryMyFilesPanelState,
  catalog: MerryMyFilesCatalog,
): MerryMyFilesVisualState {
  if (state.page === "browser") {
    return getMerryMyFilesBrowserVisualState(
      state.layout,
      state.isManaging,
    );
  }

  const item = findItem(catalog, state.fileId);
  return getMerryMyFilesDetailVisualState(item?.preview ?? "unsupported");
}

function getStateKey(
  state: MerryMyFilesPanelState,
  catalog: MerryMyFilesCatalog,
) {
  return state.page === "browser"
    ? getVisualState(state, catalog)
    : `${getVisualState(state, catalog)}:${state.fileId}`;
}

function getLayoutTargets(root: HTMLDivElement) {
  return [
    root,
    ...root.querySelectorAll<HTMLElement>("[data-my-files-layout-item]"),
  ];
}

function Icon({ className, source }: { className?: string; source: string }) {
  return <img alt="" aria-hidden="true" className={className} src={source} />;
}

function ToolbarIconButton({
  accessibleLabel,
  icon,
  onPress,
}: {
  accessibleLabel: string;
  icon: string;
  onPress?: () => void;
}) {
  return (
    <MerryButton
      aria-label={accessibleLabel}
      className="merry-my-files__toolbar-button"
      hierarchy="level0"
      icon={<Icon source={icon} />}
      onPress={onPress}
      size={34}
    />
  );
}

function ToolbarTextButton({
  children,
  hierarchy = "level1",
  icon,
  isDisabled,
  onPress,
  testId,
  title,
}: {
  children: ReactNode;
  hierarchy?: "level1" | "level3";
  icon: string;
  isDisabled?: boolean;
  onPress?: () => void;
  testId?: string;
  title?: string;
}) {
  return (
    <MerryButton
      aria-label={
        title && typeof children === "string"
          ? `${children}：${title}`
          : undefined
      }
      className="merry-my-files__toolbar-button"
      data-testid={testId}
      hierarchy={hierarchy}
      icon={<Icon source={icon} />}
      isDisabled={isDisabled}
      onPress={onPress}
      size={34}
    >
      {children}
    </MerryButton>
  );
}

function HighlightedName({
  name,
  query,
}: {
  name: string;
  query: string;
}) {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return <>{name}</>;

  const fragments: ReactNode[] = [];
  const normalizedName = name.toLocaleLowerCase();
  let cursor = 0;
  while (cursor < name.length) {
    const matchIndex = normalizedName.indexOf(normalizedQuery, cursor);
    if (matchIndex < 0) {
      fragments.push(name.slice(cursor));
      break;
    }
    if (matchIndex > cursor) fragments.push(name.slice(cursor, matchIndex));
    fragments.push(
      <mark key={`${matchIndex}-${normalizedQuery}`}>{name.slice(matchIndex, matchIndex + normalizedQuery.length)}</mark>,
    );
    cursor = matchIndex + normalizedQuery.length;
  }
  return <>{fragments}</>;
}

function InlineRenameField({
  item,
  onCancel,
  onCommit,
}: {
  item: MerryMyFilesItem;
  onCancel: () => void;
  onCommit: (displayNameBase: string) => Promise<void> | void;
}) {
  const [draft, setDraft] = useState(() => getDisplayNameBase(item));
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const isComposingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const canceledRef = useRef(false);

  const commit = async () => {
    const normalizedDraft = draft.trim();
    if (
      isComposingRef.current ||
      isSubmittingRef.current ||
      canceledRef.current
    ) {
      return;
    }
    if (!normalizedDraft) {
      setErrorMessage("文件名不能为空");
      return;
    }
    if (normalizedDraft === getDisplayNameBase(item)) {
      onCancel();
      return;
    }
    isSubmittingRef.current = true;
    setIsSaving(true);
    setErrorMessage("");
    try {
      await onCommit(normalizedDraft);
      onCancel();
    } catch {
      setErrorMessage("保存失败，请重试");
    } finally {
      isSubmittingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <TextField
      aria-label={`重命名 ${item.name}`}
      className="merry-my-files__rename-field"
      isInvalid={Boolean(errorMessage)}
    >
      <span className="merry-my-files__rename-input-shell">
        <Input
          autoFocus
          disabled={isSaving}
          onBlur={() => void commit()}
          onChange={(event) => setDraft(event.target.value)}
          onCompositionEnd={() => {
            isComposingRef.current = false;
          }}
          onCompositionStart={() => {
            isComposingRef.current = true;
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              canceledRef.current = true;
              onCancel();
            }
            if (event.key === "Enter" && !isComposingRef.current) {
              event.preventDefault();
              void commit();
            }
          }}
          value={draft}
        />
        {item.extension ? (
          <span className="merry-my-files__rename-extension">
            {item.extension}
          </span>
        ) : null}
      </span>
      {errorMessage ? (
        <span className="merry-my-files__rename-error" role="alert">
          {errorMessage}
        </span>
      ) : null}
    </TextField>
  );
}

function RenameTrigger({
  item,
  onPress,
}: {
  item: MerryMyFilesItem;
  onPress: () => void;
}) {
  return (
    <Button
      aria-label={`重命名 ${item.name}`}
      className={({ isFocusVisible }) =>
        cn(
          "merry-my-files__rename-trigger",
          isFocusVisible && "merry-my-files__rename-trigger--focus-visible",
        )
      }
      onPress={onPress}
    >
      <Icon source={editIcon} />
    </Button>
  );
}

function FileFilters({
  value,
  onChange,
}: {
  onChange: (value: MerryMyFilesFilter) => void;
  value: MerryMyFilesFilter;
}) {
  return (
    <MerryDropdownMenu
      ariaLabel="文件类型"
      className="merry-my-files__filter-popover"
      items={filterValues.map((filter) => ({
        id: filter,
        label: filterLabels[filter],
        detail: filter === value ? "✓" : undefined,
      }))}
      onAction={(nextValue) => {
        if (isFilter(nextValue)) onChange(nextValue);
      }}
    >
      <Button aria-label={`筛选文件类型，当前：${filterLabels[value]}`} className="merry-my-files__filter-trigger">
        筛选
      </Button>
    </MerryDropdownMenu>
  );
}

function SelectionCheckbox({
  accessibleLabel,
  isSelected,
  onChange,
  testId,
}: {
  accessibleLabel: string;
  isSelected: boolean;
  onChange: (isSelected: boolean) => void;
  testId?: string;
}) {
  return (
    <MerryCheckbox
      aria-label={accessibleLabel}
      data-testid={testId}
      isSelected={isSelected}
      onChange={onChange}
    />
  );
}

function SourceLabel({ label, light = false }: { label: string; light?: boolean }) {
  return (
    <span className="merry-my-files__source">
      <span>{label}</span>
      <Icon source={light ? sourceUnderlineLight : sourceUnderline} />
    </span>
  );
}

function FileThumbnail({ item }: { item: MerryMyFilesItem }) {
  if (item.previewSource && isMediaType(item.type)) {
    return (
      <span className="merry-my-files__thumbnail">
        <img alt="" src={item.previewSource} />
      </span>
    );
  }

  return (
    <span className="merry-my-files__thumbnail merry-my-files__thumbnail--file">
      <Icon source={previewTypeIcons[item.type]} />
      <span aria-hidden="true" className="merry-my-files__file-line" />
      <span aria-hidden="true" className="merry-my-files__file-line" />
    </span>
  );
}

function ItemMetadata({
  item,
  light = false,
  searchQuery = "",
}: {
  item: MerryMyFilesItem;
  light?: boolean;
  searchQuery?: string;
}) {
  return (
    <div className={cn("merry-my-files__metadata", light && "merry-my-files__metadata--light")}>
      <div className="merry-my-files__item-title-row">
        <Icon source={light ? lightTypeIcons[item.type] : typeIcons[item.type]} />
        <strong title={item.name}>
          <HighlightedName name={item.name} query={searchQuery} />
        </strong>
      </div>
      <div className="merry-my-files__item-meta-row">
        <MerryLabelTag label={item.capacityLabel} size={14} variant="normal" />
        <span>来自对话</span>
        <SourceLabel label={item.sourceLabel} light={light} />
      </div>
    </div>
  );
}

function ListItem({
  isManaging,
  isSelected,
  item,
  onChat,
  onOpen,
  onRenameStart,
  onSelectionChange,
  searchQuery,
}: {
  isManaging: boolean;
  isSelected: boolean;
  item: MerryMyFilesItem;
  onChat?: (item: MerryMyFilesItem) => void;
  onOpen: (item: MerryMyFilesItem) => void;
  onRenameStart?: (item: MerryMyFilesItem) => void;
  onSelectionChange: (isSelected: boolean) => void;
  searchQuery: string;
}) {
  return (
    <div className={cn("merry-my-files__list-item", !isManaging && onRenameStart && hasAction(item, "rename") && "merry-my-files__list-item--renameable")}>
      <Button
        aria-label={`查看 ${item.name}`}
        className={({ isFocusVisible }) =>
          cn(
            "merry-my-files__list-open",
            isFocusVisible && "merry-my-files__item-open--focus-visible",
          )
        }
        isDisabled={isManaging}
        onPress={() => onOpen(item)}
      >
        <FileThumbnail item={item} />
        <ItemMetadata item={item} searchQuery={searchQuery} />
      </Button>
      {!isManaging && onRenameStart && hasAction(item, "rename") ? (
        <Button
          aria-label={`重命名 ${item.name}`}
          className="merry-my-files__list-name-edit"
          onPress={() => onRenameStart(item)}
        >
          <span title={item.name}><HighlightedName name={item.name} query={searchQuery} /></span>
        </Button>
      ) : null}
      <div className="merry-my-files__list-trailing">
        {isManaging ? (
          <SelectionCheckbox
            accessibleLabel={`选择 ${item.name}`}
            isSelected={isSelected}
            onChange={onSelectionChange}
            testId={`my-files-select-${item.id}`}
          />
        ) : (
          <ToolbarIconButton
            accessibleLabel={`在新对话中使用 ${item.name}`}
            icon={newChatIcon}
            onPress={() => onChat?.(item)}
          />
        )}
        {isManaging ? null : <time>{item.timeLabel}</time>}
      </div>
    </div>
  );
}

function DocumentPreview({ item }: { item: MerryMyFilesItem }) {
  if (item.previewSource) {
    return <img alt="" src={item.previewSource} />;
  }

  return (
    <div aria-hidden="true" className="merry-my-files__document-preview">
      {(item.previewText ?? [item.name, item.description]).map((line, index) => (
        <p key={`${item.id}-${index}`}>{line}</p>
      ))}
    </div>
  );
}

function GridItem({
  isManaging,
  isSelected,
  item,
  onChat,
  onOpen,
  onRenameStart,
  onSelectionChange,
  searchQuery,
}: {
  isManaging: boolean;
  isSelected: boolean;
  item: MerryMyFilesItem;
  onChat?: (item: MerryMyFilesItem) => void;
  onOpen: (item: MerryMyFilesItem) => void;
  onRenameStart?: (item: MerryMyFilesItem) => void;
  onSelectionChange: (isSelected: boolean) => void;
  searchQuery: string;
}) {
  return (
    <div className="merry-my-files__grid-item">
      <Button
        aria-label={`查看 ${item.name}`}
        className={({ isFocusVisible, isHovered, isPressed }) =>
          cn(
            "merry-my-files__grid-open",
            isHovered && "merry-my-files__item-open--hovered",
            isPressed && "merry-my-files__item-open--pressed",
            isFocusVisible && "merry-my-files__item-open--focus-visible",
          )
        }
        isDisabled={isManaging}
        onPress={() => onOpen(item)}
      >
        <div className="merry-my-files__grid-preview">
          {isMediaType(item.type) && item.previewSource ? (
            <img alt="" src={item.previewSource} />
          ) : isMediaType(item.type) ? (
            <span className="merry-my-files__grid-preview-placeholder">
              <Icon source={previewTypeIcons[item.type]} />
            </span>
          ) : (
            <DocumentPreview item={item} />
          )}
        </div>
        <div className="merry-my-files__grid-overlay">
          <ItemMetadata item={item} light searchQuery={searchQuery} />
          <time>{item.timeLabel}</time>
        </div>
      </Button>
      {!isManaging && onRenameStart && hasAction(item, "rename") ? (
        <RenameTrigger item={item} onPress={() => onRenameStart(item)} />
      ) : null}
      {isManaging ? (
        <SelectionCheckbox
          accessibleLabel={`选择 ${item.name}`}
          isSelected={isSelected}
          onChange={onSelectionChange}
          testId={`my-files-select-${item.id}`}
        />
      ) : (
        <MerryButton
          aria-label={`在新对话中使用 ${item.name}`}
          className="merry-my-files__grid-chat"
          hierarchy="level0"
          icon={<Icon source={newChatLightIcon} />}
          onPress={() => onChat?.(item)}
          size={34}
        />
      )}
    </div>
  );
}

function BrowserItem({
  isManaging,
  isRenaming,
  isSelected,
  item,
  layout,
  onChat,
  onOpen,
  onRename,
  onRenameCancel,
  onRenameStart,
  onSelectionChange,
  searchQuery,
}: {
  isManaging: boolean;
  isRenaming: boolean;
  isSelected: boolean;
  item: MerryMyFilesItem;
  layout: MerryMyFilesLayout;
  onChat?: (item: MerryMyFilesItem) => void;
  onOpen: (item: MerryMyFilesItem) => void;
  onRename?: (
    item: MerryMyFilesItem,
    displayNameBase: string,
  ) => Promise<void> | void;
  onRenameCancel: () => void;
  onRenameStart: (item: MerryMyFilesItem) => void;
  onSelectionChange: (isSelected: boolean) => void;
  searchQuery: string;
}) {
  return (
    <div
      className="merry-my-files__layout-item"
      data-flip-id={`my-files-${item.id}`}
      data-my-files-layout-item={item.id}
      data-testid={`my-files-item-${item.id}`}
    >
      {layout === "list" ? (
        <ListItem
          isManaging={isManaging}
          isSelected={isSelected}
          item={item}
          onChat={onChat}
          onOpen={onOpen}
          onRenameStart={onRename ? onRenameStart : undefined}
          onSelectionChange={onSelectionChange}
          searchQuery={searchQuery}
        />
      ) : (
        <GridItem
          isManaging={isManaging}
          isSelected={isSelected}
          item={item}
          onChat={onChat}
          onOpen={onOpen}
          onRenameStart={onRename ? onRenameStart : undefined}
          onSelectionChange={onSelectionChange}
          searchQuery={searchQuery}
        />
      )}
      {isRenaming && onRename ? (
        <InlineRenameField
          item={item}
          onCancel={onRenameCancel}
          onCommit={(displayNameBase) => onRename(item, displayNameBase)}
        />
      ) : null}
    </div>
  );
}

function LoadMore({
  isManaging,
  label,
  layout,
  onPress,
}: {
  isManaging: boolean;
  label: string;
  layout: MerryMyFilesLayout;
  onPress?: () => void;
}) {
  if (layout === "list" && isManaging) {
    return (
      <MerryButton
        className="merry-my-files__load-more-button"
        hierarchy="level1"
        icon={<Icon source={moreIcon} />}
        onPress={onPress}
        size={34}
      >
        {label}
      </MerryButton>
    );
  }

  return (
    <Button
      className="merry-my-files__load-more"
      isDisabled={!onPress}
      onPress={onPress}
    >
      <span aria-hidden="true" className="merry-my-files__load-divider">
        <Icon source={loadDividerLeft} />
        <Icon source={loadDividerRight} />
      </span>
      <span className="merry-my-files__load-label">
        <Icon source={loadArrowIcon} />
        <span>{label}</span>
      </span>
      <span aria-hidden="true" className="merry-my-files__load-divider">
        <Icon source={loadDividerLeft} />
        <Icon source={loadDividerRight} />
      </span>
    </Button>
  );
}

interface BrowserPanelProps {
  catalog: MerryMyFilesCatalog;
  errorMessage?: string;
  filter: MerryMyFilesFilter;
  isLoading: boolean;
  isUploading?: boolean;
  noticeMessage?: string;
  onChat?: (item: MerryMyFilesItem) => void;
  onClose?: () => void;
  onDelete?: (items: ReadonlyArray<MerryMyFilesItem>) => void;
  onDownload?: (items: ReadonlyArray<MerryMyFilesItem>) => void;
  onFilterChange: (filter: MerryMyFilesFilter) => void;
  onLoadMore?: () => void;
  onOpen: (item: MerryMyFilesItem) => void;
  onRename?: (
    item: MerryMyFilesItem,
    displayNameBase: string,
  ) => Promise<void> | void;
  onRetry?: () => void;
  onSearch?: () => void;
  onSearchQueryChange: (query: string) => void;
  onSelectedIdsChange: (ids: ReadonlyArray<string>) => void;
  onStateChange: (state: MerryMyFilesBrowserState) => void;
  onUpload?: () => void;
  selectedIds: ReadonlyArray<string>;
  searchQuery: string;
  state: MerryMyFilesBrowserState;
}

function BrowserPanel({
  catalog,
  errorMessage,
  filter,
  isLoading,
  isUploading,
  noticeMessage,
  onChat,
  onClose,
  onDelete,
  onDownload,
  onFilterChange,
  onLoadMore,
  onOpen,
  onRename,
  onRetry,
  onSearch,
  onSearchQueryChange,
  onSelectedIdsChange,
  onStateChange,
  onUpload,
  selectedIds,
  searchQuery,
  state,
}: BrowserPanelProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(Boolean(searchQuery));
  const [renamingItemId, setRenamingItemId] = useState("");
  const toolbarRef = useRef<HTMLDivElement>(null);
  const restoreSearchFocusRef = useRef(false);

  useEffect(() => {
    if (!isSearchOpen && restoreSearchFocusRef.current) {
      toolbarRef.current?.querySelector<HTMLButtonElement>('[aria-label="搜索文件"]')?.focus();
      restoreSearchFocusRef.current = false;
    }
  }, [isSearchOpen]);
  const visibleSections = useMemo(
    () =>
      catalog.sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => matchesFilter(item, filter)),
        }))
        .filter((section) => section.items.length > 0),
    [catalog.sections, filter],
  );
  const displayedSections = isSearchOpen && searchQuery.trim()
    ? [{
        id: "search-results",
        label: "搜索内容",
        items: visibleSections.flatMap((section) => section.items),
      }]
    : visibleSections;
  const visibleItems = visibleSections.flatMap((section) => section.items);
  const selectedItems = visibleItems.filter((item) =>
    selectedIds.includes(item.id),
  );
  const canDeleteSelection =
    selectedItems.length > 0 &&
    selectedItems.every((item) => hasAction(item, "delete"));
  const selectionDeleteReason =
    selectedItems.length > 0 && !canDeleteSelection
      ? "所选文件中包含无删除权限的资源"
      : undefined;

  const updateItemSelection = (itemId: string, isSelected: boolean) => {
    const next = isSelected
      ? [...new Set([...selectedIds, itemId])].slice(0, 100)
      : selectedIds.filter((id) => id !== itemId);
    onSelectedIdsChange(next);
  };

  const updateSectionSelection = (
    section: MerryMyFilesSection,
    isSelected: boolean,
  ) => {
    const sectionIds = section.items.map((item) => item.id);
    const next = isSelected
      ? [...new Set([...selectedIds, ...sectionIds])].slice(0, 100)
      : selectedIds.filter((id) => !sectionIds.includes(id));
    onSelectedIdsChange(next);
  };

  return (
    <>
      <div
        className="merry-my-files__toolbar"
        data-my-files-layout-item="toolbar"
        ref={toolbarRef}
      >
        {isSearchOpen ? (
          <ToolbarIconButton
            accessibleLabel="返回文件列表"
            icon={backIcon}
            onPress={() => {
              onSearchQueryChange("");
              restoreSearchFocusRef.current = true;
              setIsSearchOpen(false);
            }}
          />
        ) : state.isManaging ? (
          <ToolbarIconButton
            accessibleLabel="退出批量管理"
            icon={backIcon}
            onPress={() => onStateChange({ ...state, isManaging: false })}
          />
        ) : null}
        {!isSearchOpen ? <ToolbarIconButton
          accessibleLabel="搜索文件"
          icon={searchIcon}
          onPress={() => {
            if (isSearchOpen) {
              onSearchQueryChange("");
              setIsSearchOpen(false);
            } else {
              setIsSearchOpen(true);
            }
            onSearch?.();
          }}
        /> : null}
        {isSearchOpen ? (
          <MerrySearchField
            aria-label="按文件名搜索"
            autoFocus
            className="merry-my-files__search-control"
            onChange={onSearchQueryChange}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                onSearchQueryChange("");
                restoreSearchFocusRef.current = true;
                setIsSearchOpen(false);
              }
            }}
            placeholder="搜索文件名称"
            size={34}
            value={searchQuery}
          />
        ) : <ToolbarTextButton
          icon={state.layout === "list" ? listIcon : gridIcon}
          onPress={() =>
            onStateChange({
              ...state,
              layout: state.layout === "list" ? "grid" : "list",
            })
          }
          testId="my-files-layout-toggle"
        >
          {state.layout === "list" ? "列表" : "矩阵"}
        </ToolbarTextButton>}
        <FileFilters onChange={onFilterChange} value={filter} />
        {!isSearchOpen ? <div className="merry-my-files__toolbar-actions">
          {state.isManaging ? (
            <>
              <ToolbarTextButton
                icon={deleteIcon}
                isDisabled={!canDeleteSelection}
                onPress={() => onDelete?.(selectedItems)}
                title={selectionDeleteReason}
              >
                删除
              </ToolbarTextButton>
              <ToolbarTextButton
                hierarchy="level3"
                icon={downloadIcon}
                isDisabled={selectedItems.length === 0}
                onPress={() => onDownload?.(selectedItems)}
              >
                下载
              </ToolbarTextButton>
            </>
          ) : (
            <>
              {onUpload ? (
                <ToolbarTextButton
                  icon={uploadIcon}
                  isDisabled={isUploading}
                  onPress={onUpload}
                  testId="my-files-upload-toggle"
                >
                  {isUploading ? "正在上传…" : "上传文件"}
                </ToolbarTextButton>
              ) : null}
              <ToolbarTextButton
                icon={manageIcon}
                onPress={() => {
                  setRenamingItemId("");
                  onStateChange({ ...state, isManaging: true });
                }}
                testId="my-files-manage-toggle"
              >
                批量管理
              </ToolbarTextButton>
            </>
          )}
          <ToolbarIconButton
            accessibleLabel="关闭我的文件"
            icon={closeIcon}
            onPress={onClose}
          />
        </div> : null}
      </div>

      {noticeMessage || selectionDeleteReason ? (
        <p className="merry-my-files__notice" role="status">
          {noticeMessage || selectionDeleteReason}
        </p>
      ) : null}

      <div
        className={cn(
          "merry-my-files__browser",
          `merry-my-files__browser--${state.layout}`,
        )}
      >
        <div className="merry-my-files__sections">
          {errorMessage ? (
            <div className="merry-my-files__request-state" role="alert">
              <span>{errorMessage}</span>
              {onRetry ? (
                <MerryButton hierarchy="level1" onPress={onRetry} size={34}>
                  重试
                </MerryButton>
              ) : null}
            </div>
          ) : isLoading && visibleSections.length === 0 ? (
            <div className="merry-my-files__request-state" role="status">
              正在读取文件…
            </div>
          ) : visibleSections.length === 0 ? (
            <div className="merry-my-files__request-state" role="status">
              {searchQuery.trim() ? "没有找到匹配的文件" : "这里还没有文件"}
            </div>
          ) : null}
          {displayedSections.map((section) => {
            const isSectionSelected = section.items.every((item) =>
              selectedIds.includes(item.id),
            );

            return (
              <section className="merry-my-files__section" key={section.id}>
                <div className="merry-my-files__section-heading">
                  <span>{section.label} ({section.items.length})</span>
                  {state.isManaging ? (
                    <SelectionCheckbox
                      accessibleLabel={`选择全部${section.label}`}
                      isSelected={isSectionSelected}
                      onChange={(isSelected) =>
                        updateSectionSelection(section, isSelected)
                      }
                    />
                  ) : null}
                </div>
                <div className="merry-my-files__items">
                  {section.items.map((item) => (
                    <BrowserItem
                      isManaging={state.isManaging}
                      isRenaming={!state.isManaging && renamingItemId === item.id}
                      isSelected={selectedIds.includes(item.id)}
                      item={item}
                      key={item.id}
                      layout={state.layout}
                      onChat={onChat}
                      onOpen={onOpen}
                      onRename={onRename}
                      onRenameCancel={() => setRenamingItemId("")}
                      onRenameStart={(target) => setRenamingItemId(target.id)}
                      onSelectionChange={(isSelected) =>
                        updateItemSelection(item.id, isSelected)
                      }
                      searchQuery={searchQuery}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
        <LoadMore
          isManaging={state.isManaging}
          label={catalog.loadMoreLabel}
          layout={state.layout}
          onPress={isLoading ? undefined : onLoadMore}
        />
      </div>
    </>
  );
}

function DetailMetric({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="merry-my-files__detail-metric">
      <div>
        <Icon source={icon} />
        <strong>{label}</strong>
      </div>
      <span>{value}</span>
    </div>
  );
}

interface DetailPanelProps {
  item: MerryMyFilesItem;
  onBack: () => void;
  onChat?: (item: MerryMyFilesItem) => void;
  onClose?: () => void;
  onDelete?: (items: ReadonlyArray<MerryMyFilesItem>) => void;
  onDownload?: (items: ReadonlyArray<MerryMyFilesItem>) => void;
  onOpenConversation?: (item: MerryMyFilesItem) => void;
  onRename?: (
    item: MerryMyFilesItem,
    displayNameBase: string,
  ) => Promise<void> | void;
}

function DetailPanel({
  item,
  onBack,
  onChat,
  onClose,
  onDelete,
  onDownload,
  onOpenConversation,
  onRename,
}: DetailPanelProps) {
  const [isRenaming, setIsRenaming] = useState(false);

  return (
    <>
      <div className="merry-my-files__toolbar merry-my-files__detail-toolbar">
        <ToolbarIconButton
          accessibleLabel="返回文件列表"
          icon={backIcon}
          onPress={onBack}
        />
        <div className="merry-my-files__detail-title">
          <Icon source={typeIcons[item.type]} />
          <strong title={item.name}>{item.name}</strong>
          {onRename && hasAction(item, "rename") ? (
            <RenameTrigger item={item} onPress={() => setIsRenaming(true)} />
          ) : null}
        </div>
        <div className="merry-my-files__toolbar-actions">
          {hasAction(item, "delete") ? (
            <ToolbarTextButton
              icon={deleteIcon}
              onPress={() => onDelete?.([item])}
            >
              删除
            </ToolbarTextButton>
          ) : null}
          {hasAction(item, "chat") ? (
            <ToolbarTextButton icon={newChatIcon} onPress={() => onChat?.(item)}>
              聊天
            </ToolbarTextButton>
          ) : null}
          {hasAction(item, "download") ? (
            <ToolbarTextButton
              hierarchy="level3"
              icon={downloadIcon}
              onPress={() => onDownload?.([item])}
            >
              下载
            </ToolbarTextButton>
          ) : null}
          <ToolbarIconButton
            accessibleLabel="关闭我的文件"
            icon={closeIcon}
            onPress={onClose}
          />
        </div>
      </div>

      {isRenaming && onRename ? (
        <div className="merry-my-files__detail-rename">
          <InlineRenameField
            item={item}
            onCancel={() => setIsRenaming(false)}
            onCommit={(displayNameBase) => onRename(item, displayNameBase)}
          />
        </div>
      ) : null}

      <div className="merry-my-files__detail">
        <div
          className={cn(
            "merry-my-files__detail-preview",
            item.preview === "unsupported" &&
              "merry-my-files__detail-preview--unsupported",
          )}
        >
          {item.preview === "media" && item.previewSource ? (
            item.type === "video" ? (
              <video controls preload="metadata" src={item.previewSource}>
                <track kind="captions" />
              </video>
            ) : (
              <img alt={item.name} src={item.previewSource} />
            )
          ) : (
            <div className="merry-my-files__unsupported">
              <Icon source={unsupportedFileIcon} />
              <span>暂不支持文件预览，请下载后查看详情</span>
            </div>
          )}
        </div>

        <div className="merry-my-files__detail-metrics">
          <DetailMetric
            icon={timeIcon}
            label="生成时间"
            value={item.createdAtLabel}
          />
          <DetailMetric
            icon={dimensionsIcon}
            label="文件尺寸"
            value={item.dimensionsLabel}
          />
          <DetailMetric
            icon={capacityIcon}
            label="储存容量"
            value={item.capacityLabel}
          />
        </div>

        <section className="merry-my-files__detail-section">
          <div className="merry-my-files__detail-section-heading">
            <span>
              <Icon source={conversationIcon} />
              <strong>对话原文</strong>
            </span>
            {item.sourceConversationId && onOpenConversation ? (
              <Button
                aria-label="打开对话原文"
                className="merry-my-files__external-link"
                onPress={() => onOpenConversation(item)}
              >
                <Icon source={externalLinkIcon} />
              </Button>
            ) : null}
          </div>
          <p>
            {item.sourceConversationId
              ? item.description
              : "来源对话暂不可用"}
          </p>
        </section>

        <section className="merry-my-files__detail-section">
          <div className="merry-my-files__detail-section-heading">
            <span>
              <Icon source={uploadIcon} />
              <strong>上传的参考</strong>
            </span>
          </div>
          <div className="merry-my-files__references">
            {item.referenceSources.map((source, index) => (
              <div className="merry-my-files__reference" key={`${source}-${index}`}>
                <img alt={`参考图 ${index + 1}`} src={source} />
              </div>
            ))}
            {item.referenceSources.length === 0 ? (
              <span className="merry-my-files__references-empty">
                暂无可用参考
              </span>
            ) : null}
          </div>
        </section>
      </div>
    </>
  );
}

export function MerryMyFilesPanel({
  "aria-label": ariaLabel = "我的文件",
  "data-testid": testId,
  catalog,
  className,
  defaultSelectedIds = [],
  defaultState = defaultBrowserState,
  errorMessage,
  filter,
  isLoading = false,
  isUploading = false,
  noticeMessage,
  onChat,
  onClose,
  onDelete,
  onDownload,
  onLoadMore,
  onOpenConversation,
  onRename,
  onRetry,
  onSearch,
  onSearchQueryChange,
  onFilterChange,
  onSelectedIdsChange,
  onStateChange,
  onUpload,
  selectedIds,
  searchQuery,
  state,
}: MerryMyFilesPanelProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pendingLayoutRef = useRef<ReturnType<typeof Flip.getState> | null>(
    null,
  );
  const isInitialRequestRef = useRef(true);
  const isInitialRenderRef = useRef(true);
  const previousBrowserRef = useRef<MerryMyFilesBrowserState>(
    defaultState.page === "browser" ? defaultState : defaultBrowserState,
  );
  const [internalState, setInternalState] =
    useState<MerryMyFilesPanelState>(defaultState);
  const [renderedState, setRenderedState] = useState<MerryMyFilesPanelState>(
    state ?? defaultState,
  );
  const [internalFilter, setInternalFilter] = useState<MerryMyFilesFilter>("all");
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const [internalSelectedIds, setInternalSelectedIds] =
    useState<ReadonlyArray<string>>(defaultSelectedIds);
  const requestedState = state ?? internalState;
  const activeFilter = filter ?? internalFilter;
  const activeSearchQuery = searchQuery ?? internalSearchQuery;
  const activeSelectedIds = selectedIds ?? internalSelectedIds;
  const requestedKey = getStateKey(requestedState, catalog);
  const renderedKey = getStateKey(renderedState, catalog);
  const requestedVisualState = getVisualState(requestedState, catalog);
  const renderedVisualState = getVisualState(renderedState, catalog);
  const renderedVariant = getMerryMyFilesVariant(renderedVisualState);
  const requestedVariant = getMerryMyFilesVariant(requestedVisualState);
  const detailItem =
    renderedState.page === "detail"
      ? findItem(catalog, renderedState.fileId)
      : undefined;

  const changeState = useCallback(
    (nextState: MerryMyFilesPanelState) => {
      if (nextState.page === "browser") previousBrowserRef.current = nextState;
      if (state === undefined) setInternalState(nextState);
      onStateChange?.(nextState);
    },
    [onStateChange, state],
  );

  const changeSelectedIds = useCallback(
    (nextIds: ReadonlyArray<string>) => {
      if (selectedIds === undefined) setInternalSelectedIds(nextIds);
      onSelectedIdsChange?.(nextIds);
    },
    [onSelectedIdsChange, selectedIds],
  );

  const openItem = useCallback(
    (item: MerryMyFilesItem) => {
      if (requestedState.page === "browser" && requestedState.isManaging) return;
      if (requestedState.page === "browser") {
        previousBrowserRef.current = requestedState;
      }
      changeState({ fileId: item.id, page: "detail" });
    },
    [changeState, requestedState],
  );

  useGSAP(
    () => {
      const root = rootRef.current;
      const content = contentRef.current;
      if (!root || !content) return;

      if (isInitialRequestRef.current) {
        isInitialRequestRef.current = false;
        return;
      }

      const currentTargets = getLayoutTargets(root);
      Flip.killFlipsOf(currentTargets, true);
      gsap.killTweensOf([root, content, ...currentTargets]);
      pendingLayoutRef.current = null;

      if (prefersReducedMotion()) {
        setRenderedState(requestedState);
        gsap.set(root, { clearProps: "height,transform,width,willChange" });
        gsap.set(content, {
          autoAlpha: 1,
          clearProps: "opacity,transform,visibility,willChange",
        });
        root.dataset.myFilesAnimating = "false";
        return;
      }

      const isBrowserLayoutChange =
        renderedState.page === "browser" && requestedState.page === "browser";

      root.dataset.myFilesAnimating = "true";

      if (isBrowserLayoutChange) {
        pendingLayoutRef.current = Flip.getState(currentTargets, {
          props: "opacity",
        });
        setRenderedState(requestedState);
        return;
      }

      gsap.set([root, content], { willChange: "height, opacity" });
      gsap.to(root, {
        duration: motion.resize,
        ease: "power3.inOut",
        height: requestedVariant.height,
        overwrite: true,
      });
      gsap.to(content, {
        autoAlpha: 0,
        duration: motion.contentExit,
        ease: "power2.in",
        overwrite: true,
        onComplete: () => setRenderedState(requestedState),
        onInterrupt: () => {
          root.dataset.myFilesAnimating = "false";
        },
      });
    },
    { dependencies: [requestedKey], scope: rootRef },
  );

  useGSAP(
    () => {
      const root = rootRef.current;
      const content = contentRef.current;
      if (!root || !content) return;

      if (isInitialRenderRef.current) {
        isInitialRenderRef.current = false;
        root.dataset.myFilesAnimating = "false";
        return;
      }

      const previousLayout = pendingLayoutRef.current;
      pendingLayoutRef.current = null;

      if (prefersReducedMotion()) {
        root.dataset.myFilesAnimating = "false";
        gsap.set([root, content], {
          clearProps: "height,opacity,transform,visibility,width,willChange",
        });
        return;
      }

      if (previousLayout) {
        const targets = getLayoutTargets(root);
        gsap.set(targets, { willChange: "height, opacity, transform, width" });
        Flip.from(previousLayout, {
          duration: motion.layout,
          ease: "power3.inOut",
          nested: true,
          prune: true,
          scale: false,
          stagger: 0.012,
          onComplete: () => {
            root.dataset.myFilesAnimating = "false";
            gsap.set(targets, { clearProps: "willChange" });
          },
          onInterrupt: () => {
            root.dataset.myFilesAnimating = "false";
            gsap.set(targets, { clearProps: "willChange" });
          },
        });
        return;
      }

      gsap.fromTo(
        content,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: motion.contentEnter,
          ease: "power2.out",
          overwrite: true,
          onComplete: () => {
            root.dataset.myFilesAnimating = "false";
            gsap.set([root, content], {
              clearProps: "height,opacity,visibility,willChange",
            });
          },
          onInterrupt: () => {
            root.dataset.myFilesAnimating = "false";
          },
        },
      );
    },
    { dependencies: [renderedKey], scope: rootRef },
  );

  const style: MerryMyFilesStyle = {
    "--merry-my-files-height": `${renderedVariant.height}px`,
    "--merry-my-files-width": `${renderedVariant.width}px`,
  };

  return (
    <div
      aria-label={ariaLabel}
      className={cn("merry-my-files", className)}
      data-figma-component-id="719:105765"
      data-figma-source-node-ids="719:105765,719:107077,719:109986,719:111108,719:112194,720:100306,720:101665,720:103035,720:107205,720:105227,720:106220"
      data-figma-node-id={getMerryMyFilesVariant(requestedVisualState).figmaNodeId}
      data-my-files-animating="false"
      data-my-files-view={requestedVisualState}
      data-testid={testId}
      ref={rootRef}
      role="region"
      style={style}
    >
      <span aria-hidden="true" className="merry-my-files__glass" />
      <div className="merry-my-files__content" ref={contentRef}>
        {renderedState.page === "browser" ? (
          <BrowserPanel
            catalog={catalog}
            errorMessage={errorMessage}
            filter={activeFilter}
            isLoading={isLoading}
            isUploading={isUploading}
            noticeMessage={noticeMessage}
            onChat={onChat}
            onClose={onClose}
            onDelete={onDelete}
            onDownload={onDownload}
            onFilterChange={(nextFilter) => {
              if (filter === undefined) setInternalFilter(nextFilter);
              onFilterChange?.(nextFilter);
            }}
            onLoadMore={onLoadMore}
            onOpen={openItem}
            onRename={onRename}
            onRetry={onRetry}
            onSearch={onSearch}
            onSearchQueryChange={(nextQuery) => {
              if (searchQuery === undefined) setInternalSearchQuery(nextQuery);
              onSearchQueryChange?.(nextQuery);
            }}
            onSelectedIdsChange={changeSelectedIds}
            onStateChange={changeState}
            onUpload={onUpload}
            selectedIds={activeSelectedIds}
            searchQuery={activeSearchQuery}
            state={renderedState}
          />
        ) : detailItem ? (
          <DetailPanel
            item={detailItem}
            onBack={() => changeState(previousBrowserRef.current)}
            onChat={onChat}
            onClose={onClose}
            onDelete={onDelete}
            onDownload={onDownload}
            onOpenConversation={onOpenConversation}
            onRename={onRename}
          />
        ) : (
          <div
            className="merry-my-files__request-state merry-my-files__request-state--detail"
            role={errorMessage ? "alert" : "status"}
          >
            <span>{errorMessage || "正在读取文件详情…"}</span>
            {errorMessage && onRetry ? (
              <MerryButton hierarchy="level1" onPress={onRetry} size={34}>
                重试
              </MerryButton>
            ) : null}
          </div>
        )}
      </div>
      <span aria-hidden="true" className="merry-my-files__stroke" />
    </div>
  );
}
